<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { fetchFeaturedStamps, fetchStampImages, addFeaturedStamp, editFeaturedStamp } from "./api";

const route = useRoute(), router = useRouter();
// The route param is the item's positional `index` in the featured_list array
// (there is no per-item id on the backend), so it must be compared as a number.
const index = computed(() => (route.params.id !== undefined ? Number(route.params.id) : null));
const isEdit = computed(() => index.value !== null);

const loading = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), notFound = ref(false);
const stamps = ref([]); // available stamp names to pick from ({ name, imageUrl })
let nextIndex = 0;

const form = ref({ featured_stamp: "", bonus_points: 0, startDate: "", endDate: "" });

onMounted(async () => {
  try { stamps.value = await fetchStampImages(); } catch { /* non-fatal */ }
  loading.value = true;
  try {
    const list = await fetchFeaturedStamps();
    nextIndex = list.reduce((max, s) => Math.max(max, s.index ?? -1), -1) + 1;
    if (!isEdit.value) return;
    const item = list.find((s) => s.index === index.value);
    if (!item) { notFound.value = true; return; }
    const [start, end] = Array.isArray(item.target_range) ? item.target_range : ["", ""];
    form.value = { featured_stamp: item.featured_stamp ?? "", bonus_points: item.bonus_points ?? 0, startDate: start ?? "", endDate: end ?? "" };
  } catch { error.value = "Couldn't load featured stamp."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.featured_stamp) { validationError.value = "Select a stamp."; return; }
  if (!f.startDate || !f.endDate) { validationError.value = "Start and end dates are required."; return; }
  const target_range = [f.startDate, f.endDate];
  const payload = {
    featured_stamp: f.featured_stamp,
    bonus_points: Number(f.bonus_points) || 0,
    target_range,
    displayRange: target_range.join(" ~ "),
  };
  saving.value = true;
  try {
    if (isEdit.value) await editFeaturedStamp({ ...payload, index: index.value });
    else await addFeaturedStamp({ ...payload, index: nextIndex });
    success.value = true; setTimeout(() => router.push({ name: "featured-stamp" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'featured-stamp' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Featured Stamp" : "Add Featured Stamp" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Featured stamp not found.</VAlert>
    <VCard v-else class="pa-4" max-width="600">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <VSelect
          v-model="form.featured_stamp"
          label="Featured Stamp"
          :items="stamps.map(s => s.name)"
          class="mb-4"
        />
        <VTextField v-model.number="form.bonus_points" label="Additional Points" type="number" class="mb-4" />
        <VRow>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" label="Start Date" type="date" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" label="End Date" type="date" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-2">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Add" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'featured-stamp' })">Cancel</VBtn>
        </div>
      </form>
    </VCard>
  </section>
</template>
